import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Camera, Upload, X, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { UserPhotoService } from '@/services/userPhotoService';

interface UserPhotoUploadProps {
  userId: number;
  currentPhoto?: string;
  onPhotoUpdate?: (filename: string) => void;
}

const UserPhotoUpload: React.FC<UserPhotoUploadProps> = ({
  userId,
  currentPhoto,
  onPhotoUpdate
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar tipo de arquivo
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Erro",
          description: "Por favor, selecione apenas arquivos de imagem.",
          variant: "destructive",
        });
        return;
      }

      // Validar tamanho (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "Erro",
          description: "A imagem deve ter no máximo 5MB.",
          variant: "destructive",
        });
        return;
      }

      setSelectedFile(file);
      
      // Criar preview
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setIsUploading(true);
      
      // Fazer upload da foto
      const filename = await UserPhotoService.uploadUserPhoto(selectedFile, userId);
      
      // Atualizar no banco de dados
      await UserPhotoService.updateUserPhoto(userId, filename);
      
      toast({
        title: "Sucesso",
        description: "Foto de perfil atualizada com sucesso!",
      });

      // Limpar estado
      setSelectedFile(null);
      setPreviewUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Notificar componente pai
      if (onPhotoUpdate) {
        onPhotoUpdate(filename);
      }

      // Disparar evento para atualizar o header imediatamente
      window.dispatchEvent(new CustomEvent('userUpdated'));

    } catch (error) {
      console.error('Erro ao fazer upload da foto:', error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar a foto de perfil.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = async () => {
    try {
      // Remover foto do banco de dados
      await UserPhotoService.updateUserPhoto(userId, '');
      
      toast({
        title: "Sucesso",
        description: "Foto de perfil removida com sucesso!",
      });

      // Notificar componente pai
      if (onPhotoUpdate) {
        onPhotoUpdate('');
      }

      // Disparar evento para atualizar o header imediatamente
      window.dispatchEvent(new CustomEvent('userUpdated'));
    } catch (error) {
      console.error('Erro ao remover foto:', error);
      toast({
        title: "Erro",
        description: "Não foi possível remover a foto de perfil.",
        variant: "destructive",
      });
    }
  };

  const getCurrentPhotoUrl = () => {
    if (currentPhoto) {
      return UserPhotoService.getUserPhotoUrl(userId, currentPhoto);
    }
    return null;
  };

  return (
    <Card className="w-full max-w-md">
      <CardContent className="p-6">
        <div className="flex flex-col items-center space-y-4">
          {/* Avatar atual */}
          <div className="relative">
            <Avatar className="w-24 h-24">
              <AvatarImage 
                src={previewUrl || getCurrentPhotoUrl() || undefined} 
                alt="Foto do usuário" 
              />
              <AvatarFallback className="text-2xl">
                <Camera className="w-8 h-8" />
              </AvatarFallback>
            </Avatar>
            
            {/* Botão para remover foto atual */}
            {currentPhoto && !selectedFile && (
              <Button
                size="sm"
                variant="destructive"
                className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                onClick={handleRemovePhoto}
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>

          {/* Input de arquivo */}
          <div className="w-full">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              className="w-full"
              disabled={isUploading}
            >
              <Upload className="w-4 h-4 mr-2" />
              {selectedFile ? 'Trocar Foto' : 'Selecionar Foto'}
            </Button>
          </div>

          {/* Preview e ações */}
          {selectedFile && (
            <div className="w-full space-y-2">
              <div className="text-sm text-gray-600 text-center">
                {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </div>
              
              <div className="flex space-x-2">
                <Button
                  onClick={handleUpload}
                  disabled={isUploading}
                  className="flex-1"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    'Confirmar'
                  )}
                </Button>
                
                <Button
                  onClick={handleCancel}
                  variant="outline"
                  disabled={isUploading}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}

          {/* Informações */}
          <div className="text-xs text-gray-500 text-center">
            Formatos aceitos: JPG, PNG, GIF<br />
            Tamanho máximo: 5MB
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default UserPhotoUpload;
